import { Feature } from "components/feature";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { Radio, RadioOption } from "components/shared/radio";
import React from "react";
import { useCurrentUser } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { useCommitUserPreference } from "./account-settings-hooks";
import { UserTogglePref } from "./account-settings-toggle";

const clientAssignmentDashboardOptions: RadioOption[] = [
    {
        key: "myself",
        text: "Myself",
        helpText: "On dashboard pages, default to showing clients assigned to me.",
    },
    {
        key: "everyone",
        text: "Everyone",
        helpText: "On dashboard pages, default to showing clients assigned to anyone.",
    },
];

export const AccountSettingsClient: React.FC<unknown> = () => {
    const user = useCurrentUser();
    const updateClientAssignmentFilter = useCommitUserPreference(
        "preferredClientAssignmentFilter",
    );
    const { settings } = useTenantSettings();

    return (
        <Feature name="placement">
            <div className="space-y-4 lg:space-y-8">
                <ContentFull>
                    <ContentHeader>Clients</ContentHeader>
                    <div className="space-y-4">
                        <UserTogglePref
                            field="preferAge"
                            label="Default to Age on Client Intake form"
                            description='Use "Age" instead of "Birthday" by default when entering client details.'
                        />
                        <Radio
                            label={`Default "Filter by Employee" on Dashboard pages`}
                            value={
                                user.preferences.preferredClientAssignmentFilter ||
                                "myself"
                            }
                            onChange={updateClientAssignmentFilter}
                            options={clientAssignmentDashboardOptions}
                        />
                    </div>
                </ContentFull>
                <ContentFull>
                    <ContentHeader>Client referral emails</ContentHeader>
                    <ContentSubheader>
                        Default options when emailing Client Intakes.
                    </ContentSubheader>
                    <div className="space-y-4">
                        <UserTogglePref
                            field="useBulkCommunityEmail"
                            label="Use bulk community email"
                            description="Show options to email any/all potential matched communities at the same time."
                        />
                    </div>
                </ContentFull>
                <ContentFull>
                    <ContentHeader>Client referral PDFs</ContentHeader>
                    <ContentSubheader>
                        Default options when downloading or emailing Client referral PDFs.
                    </ContentSubheader>
                    <div className="space-y-4">
                        <UserTogglePref
                            field="showAdditionalContactsForReferrals"
                            label="Share Additional Contacts by default"
                            description="Include the client's Additional Contacts in the generated PDF by default."
                        />
                        <UserTogglePref
                            field="showContactPhoneNumbersForReferrals"
                            label="Share contact phone numbers by default"
                            description="Include the client contacts' phone numbers in the generated PDF by default."
                        />
                        <UserTogglePref
                            field="showContactEmailAddrsForReferrals"
                            label="Share contact email addresses by default"
                            description="Include the client contacts' email addresses in the generated PDF by default."
                        />
                        <UserTogglePref
                            field="showContactLocationsForReferrals"
                            label="Share contact locations by default"
                            description="Include the client contacts' location details in the generated PDF by default."
                        />
                        <UserTogglePref
                            field="showClientBudget"
                            label="Share budget by default"
                            description="Include the client budget in the generated PDF by default."
                        />
                        <UserTogglePref
                            field="showClientLocation"
                            label="Share current location by default"
                            description="Include the client current location details in the generated PDF by default."
                        />
                        {settings.showPhysicianContacts ? (
                            <UserTogglePref
                                field="showCareProvidersForReferrals"
                                label="Share care providers by default"
                                description="Include the client's care providers in generated PDF by default."
                            />
                        ) : null}
                        {settings.showInsuranceInfo ? (
                            <UserTogglePref
                                field="showHealthInsuranceForReferrals"
                                label="Share health insurance by default"
                                description="Include the client's health insurance details in generated PDF by default."
                            />
                        ) : null}
                    </div>
                </ContentFull>
            </div>
        </Feature>
    );
};
